import React, {
  Suspense,
  useCallback,
  useDeferredValue,
  useMemo,
  useState,
} from "react";
import { WorkspaceRoutinesStackScreenProps } from "../WorkspaceRoutinesNavigation";
import { HeaderButton } from "@/src/components/mobile/Button/HeaderButtons";
import { WorkspaceDetailNavigationHeader } from "../../WorkspaceDetailNavigationHeader";
import { useAllChecklistLabel } from "../useAllChecklistLabel";
import {
  AHEmptyScreen,
  AHIcon,
  AHSeparator,
  Box,
  ListItem,
  TouchableBox,
} from "arkh";
import { OrgSearchInput } from "../../OrgSearchInput";
import { LoadingList } from "../../WorkspaceProducts/LoadingList";
import { useLazyLoadQuery, graphql, usePaginationFragment } from "react-relay";
import { useWorkspaceId } from "../../workspaceId";
import { WorkspaceArchivedRoutinesQuery } from "./__generated__/WorkspaceArchivedRoutinesQuery.graphql";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { WorkspaceArchivedRoutinesList_archiveRoutines$key } from "./__generated__/WorkspaceArchivedRoutinesList_archiveRoutines.graphql";
import { useOrgHomeDetailLayout } from "../../useOrgHomeDetailContainerStyle";
import { FlatList } from "react-native";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { ColumnTitle } from "../../SortableColumnTitle";
import { WorkspaceEmptyListItem } from "../../WorkspaceEmptyListItem";
import { WorkspaceRoutinesListItemContent } from "../WorkspaceComplianceRoutines/WorkspaceRoutinesListItem";
import { Image } from "expo-image";
import { FormattedMessage } from "react-intl";

function WorkspaceArchivedRoutinesListItemContainer(props: {
  title: React.ReactNode;
  more: React.ReactNode;
}) {
  return (
    <Box style={{ flexDirection: "row", alignItems: "center" }}>
      <Box style={{ flex: 1, flexDirection: "row", alignItems: "center" }}>
        <Box style={{ width: "40%" }} spaceRight="medium">
          {props.title}
        </Box>
      </Box>
      <Box style={{ width: 20 }}>{props.more}</Box>
    </Box>
  );
}

function WorkspaceArchivedRoutinesListItemHeader() {
  const intl_tokens = useIntlTokens();
  return (
    <Box color="surface-background-light">
      <ListItem disabled border="border-primary" displayMode="TABLE">
        <WorkspaceArchivedRoutinesListItemContainer
          title={<ColumnTitle>{intl_tokens.Title}</ColumnTitle>}
          more={null}
        />
      </ListItem>
      <AHSeparator />
    </Box>
  );
}

function WorkspaceArchivedRoutinesList(props: {
  workspace: WorkspaceArchivedRoutinesList_archiveRoutines$key;
  header: React.ReactElement;
  openRoutine: (routineWorkspaceId: string, sequenceNumber: number) => void;
  openRoutineEdit: (routineWorkspaceId: string) => void;
}) {
  const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment(
    graphql`
      fragment WorkspaceArchivedRoutinesList_archiveRoutines on Workspace
      @refetchable(
        queryName: "WorkspaceArchivedRoutinesList_WorkspaceRefetchQuery"
      ) {
        id
        hasArchivedRoutine
        archivedRoutines(first: $first, after: $after, query: $query)
          @connection(
            key: "WorkspaceArchivedRoutinesList_archiveRoutines_archivedRoutines"
          ) {
          edges {
            cursor
            node {
              id
              title
              ...WorkspaceRoutinesListItemContent_routine
              lastWorkspaceOccasion(workspaceId: $workspaceId) {
                id
                sequenceNumber
              }
            }
          }
        }
      }
    `,
    props.workspace
  );

  const listData = useMemo(() => {
    return data.archivedRoutines.edges.filter((e) => e.node?.id);
  }, [data.archivedRoutines.edges]);

  const detailLayout = useOrgHomeDetailLayout();

  const onReachEnd = useCallback(() => {
    if (hasNext && !isLoadingNext) {
      loadNext(30);
    }
  }, [hasNext, isLoadingNext, loadNext]);

  const renderItem = useCallback(
    ({ item, index }: { item: (typeof listData)[number]; index: number }) => {
      const sequenceNumber = item.node.lastWorkspaceOccasion?.sequenceNumber;
      const canOpenRoutineOccasion =
        sequenceNumber !== null && sequenceNumber !== undefined;
      return (
        <ListItem
          displayMode="TABLE"
          isLast={index + 1 === listData.length}
          onPress={() => {
            if (canOpenRoutineOccasion) {
              props.openRoutine(item.node.id, sequenceNumber);
            }
          }}
          disabled={!canOpenRoutineOccasion}
          border="border-primary"
        >
          <WorkspaceArchivedRoutinesListItemContainer
            title={
              <Box style={{ opacity: canOpenRoutineOccasion ? 1 : 0.5 }}>
                <WorkspaceRoutinesListItemContent routine={item.node} />
              </Box>
            }
            more={
              <TouchableBox
                onPress={() => {
                  props.openRoutineEdit(item.node.id);
                }}
              >
                <AHIcon type="more-vertical" size="medium" />
              </TouchableBox>
            }
          />
        </ListItem>
      );
    },
    [listData.length, props]
  );

  if (!data.hasArchivedRoutine) {
    const imageSource = require("../../WorkspaceTodos/WorkspaceTodosArchived/workspace_todo_archive_empty.png");
    return (
      <AHEmptyScreen
        title={
          <FormattedMessage
            defaultMessage="Archived Checklists"
            description="WorkspaceArchivedRoutines header title"
          />
        }
        subtitle={
          <FormattedMessage
            defaultMessage="No archived checklist available. Once a checklist is archived, it will be listed here."
            description="WorkspaceArchivedRoutines empty subtitle"
          />
        }
        illustration={
          <Box style={{ flex: 1, minWidth: 500 }}>
            <Image
              contentFit="contain"
              contentPosition="top center"
              style={{ flex: 1 }}
              source={imageSource}
            />
            <Image
              contentFit="contain"
              style={{ flex: 1 }}
              source={imageSource}
            />
            <Image
              contentFit="contain"
              style={{ flex: 1 }}
              source={imageSource}
            />
          </Box>
        }
      />
    );
  }

  return (
    <FlatList
      ListHeaderComponent={props.header}
      onEndReached={onReachEnd}
      contentContainerStyle={detailLayout.contentContainerStyle}
      data={listData}
      renderItem={renderItem}
      ItemSeparatorComponent={AHSeparator}
      stickyHeaderIndices={[0]}
      ListEmptyComponent={WorkspaceEmptyListItem}
      keyExtractor={(item) => item.node.id}
    />
  );
}

function WorkspaceArchivedRoutinesComponent(props: {
  fetchKey: string;
  openRoutine: (routineWorkspaceId: string, sequenceNumber: number) => void;
  openRoutineEdit: (routineWorkspaceId: string) => void;
}) {
  const workspaceId = useWorkspaceId();

  const [search, setSearch] = useState("");

  const query = useDeferredValue(search);

  const data = useLazyLoadQuery<WorkspaceArchivedRoutinesQuery>(
    graphql`
      query WorkspaceArchivedRoutinesQuery(
        $workspaceId: ID!
        $after: String
        $first: Int
        $query: String
      ) {
        workspace(id: $workspaceId) {
          id
          hasArchivedRoutine
          ...WorkspaceArchivedRoutinesList_archiveRoutines
        }
      }
    `,
    {
      workspaceId,
      query,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const header = useMemo(() => {
    return (
      <Box color="surface-background-light" spaceTop="medium">
        <OrgSearchInput value={search} onChangeText={setSearch} />
        <WorkspaceArchivedRoutinesListItemHeader />
      </Box>
    );
  }, [search]);

  return (
    <Suspense fallback={<LoadingList header={header} />}>
      <WorkspaceArchivedRoutinesList
        workspace={data.workspace}
        openRoutine={props.openRoutine}
        openRoutineEdit={props.openRoutineEdit}
        header={header}
      />
    </Suspense>
  );
}

export function WorkspaceArchivedRoutines(
  props: WorkspaceRoutinesStackScreenProps<"WorkspaceArchivedRoutines">
) {
  const headerBackTitle = useAllChecklistLabel();
  const openRoutine = useCallback(
    (routineWorkspaceId: string, sequenceNumber: number) => {
      props.navigation.navigateDeprecated("WorkspaceRoutineOccasion", {
        routineId: routineWorkspaceId,
        sequenceNumber,
        archived: true,
      });
    },
    [props.navigation]
  );
  const workspaceId = useWorkspaceId();

  const openRoutineEdit = useCallback(
    (routineWorkspaceId: string) => {
      props.navigation.navigate("RoutineEdit", {
        id: routineWorkspaceId,
        orgWorkspaceId: workspaceId,
      });
    },
    [props.navigation, workspaceId]
  );
  return (
    <>
      <WorkspaceDetailNavigationHeader
        left={
          <HeaderButton
            type="WEB-BACK"
            onPress={() =>
              props.navigation.navigateDeprecated("WorkspaceRoutinesTab", {})
            }
            customLabel={headerBackTitle}
          />
        }
        right={null}
      />

      <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
        {(fetchKey) => (
          <WorkspaceArchivedRoutinesComponent
            fetchKey={fetchKey}
            openRoutine={openRoutine}
            openRoutineEdit={openRoutineEdit}
          />
        )}
      </LazyQueryNetworkBoundrary>
    </>
  );
}
